import { createAsyncThunk } from '@reduxjs/toolkit';

import { extractErrorMessage } from '@/shared/utils/errorHandler';

import {
  forgotPassword,
  login as loginRequest,
  resetPassword,
  signup as signupRequest,
  signout,
} from '../api/auth.service';

import type { AuthUser } from '../types/auth';

interface LoginPayload {
  email: string;
  password: string;
}

interface SignupPayload {
  name: string;
  email: string;
  password: string;
}

interface ForgotPasswordPayload {
  email: string;
}

interface ResetPasswordPayload {
  token: string;
  password: string;
}

export interface AuthResponse {
  user: AuthUser;
  token: string;
}

export const loginThunk = createAsyncThunk<
  AuthResponse,
  LoginPayload,
  { rejectValue: string }
>(
  'auth/login',
  async (
    { email, password },
    { rejectWithValue }
  ) => {
    try {
      const result = await loginRequest(
        email,
        password
      );

      return {
        user: result.user,
        token: result.token,
      };
    } catch (error) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          'Login failed'
        )
      );
    }
  }
);

export const signupThunk = createAsyncThunk<
  AuthResponse,
  SignupPayload,
  { rejectValue: string }
>(
  'auth/signup',
  async (
    { name, email, password },
    { rejectWithValue }
  ) => {
    try {
      const result = await signupRequest(
        email,
        password,
        name
      );

      return {
        user: result.user,
        token: result.token,
      };
    } catch (error) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          'Signup failed'
        )
      );
    }
  }
);

export const forgotPasswordThunk = createAsyncThunk<
  boolean,
  ForgotPasswordPayload,
  { rejectValue: string }
>(
  'auth/forgotPassword',
  async (
    { email },
    { rejectWithValue }
  ) => {
    try {
      await forgotPassword(email);

      return true;
    } catch (error) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          'Password reset request failed'
        )
      );
    }
  }
);

export const resetPasswordThunk = createAsyncThunk<
  boolean,
  ResetPasswordPayload,
  { rejectValue: string }
>(
  'auth/resetPassword',
  async (
    { token, password },
    { rejectWithValue }
  ) => {
    try {
      await resetPassword(
        token,
        password
      );

      return true;
    } catch (error) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          'Password reset failed'
        )
      );
    }
  }
);

export const logoutThunk = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>(
  'auth/logout',
  async (
    _,
    { rejectWithValue }
  ) => {
    try {
      await signout();

      return true;
    } catch (error) {
      return rejectWithValue(
        extractErrorMessage(
          error,
          'Logout failed'
        )
      );
    }
  }
);